import React, { useState, useRef, useEffect, useMemo } from "react";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Icon from "../../../components/Icon";
import CardProductInfo from "../../../components/CardProductInfo";
import Pagination from "../../../components/Pagination";
import InputSelect from "../../../components/InputSelect";
import RangeSlider from "../../../components/RangeSlider";
import SearchProducts, { MIN_SEARCH_LENGTH } from "../../../components/SearchProducts";
import { PlanFeatureUpgradeModal } from "../../../components/PlanFeatureUpgradeModal";
import { createOptions } from "../../../helpers/selects";
import { convertIntToMoney, formatNumberWithDot } from "../../../helpers/conversions";
import { hasPermission, hasPlanFeature } from "../../../services/auth";
import api from "../../../services/api";

const PER_PAGE = 24;

// Estoque mínimo padrão: por padrão não filtra por estoque (0 = "Qualquer"),
// então itens zerados também aparecem.
const DEFAULT_STOCK_MIN = 0;

const STOCK_PRESETS = [
    { label: "Qualquer", value: 0 },
    { label: "100+", value: 100 },
    { label: "500+", value: 500 },
    { label: "1.000+", value: 1000 },
    { label: "5.000+", value: 5000 },
];

const ordernations = [
    { label: "Relevância", value: null },
    { label: "Mais recentes", orderParm: "created_at", orderSort: "desc", value: 0 },
    { label: "Ordem alfabética", orderParm: "name", orderSort: "asc", value: 1 },
    { label: "Menor preço", orderParm: "price", orderSort: "asc", value: 3 },
    { label: "Maior preço", orderParm: "price", orderSort: "desc", value: 4 },
];

// Variante compacta do react-select para o trilho de filtros (36px).
const compactSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '36px',
        height: '36px',
        fontSize: '13px',
        borderRadius: '8px',
        borderColor: state.isFocused ? '#4D3DAB' : '#DDD9F0',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(77, 61, 171, 0.16)' : 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': {
            borderColor: state.isFocused ? '#4D3DAB' : '#A89EDF',
        },
    }),
    valueContainer: (provided) => ({ ...provided, height: '34px', padding: '0 10px' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '34px' }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '6px' }),
};

const calculateTotalStock = (subProducts) => {
    if (!Array.isArray(subProducts)) return 0;
    return subProducts.reduce((total, item) => total + (item.stock || 0), 0);
};

const rangesEqual = (a, b) => a[0] === b[0] && a[1] === b[1];

/**
 * Filtro de estoque mínimo: campo digitável (aplicado no blur/Enter) +
 * atalhos rápidos. Valor 0 = "Qualquer" (sem filtro).
 */
const StockMinFilter = ({ value, onChange }) => {
    const [str, setStr] = useState(value > 0 ? formatNumberWithDot(value) : '');

    useEffect(() => {
        setStr(value > 0 ? formatNumberWithDot(value) : '');
    }, [value]);

    // Máscara ao vivo: reagrupa os dígitos com pontos a cada tecla, sem
    // esperar o blur, então o campo nunca exibe o número "cru".
    const handleChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '');
        setStr(digits ? formatNumberWithDot(parseInt(digits, 10)) : '');
    };

    const commit = () => {
        const digits = String(str).replace(/\D/g, '');
        const v = digits ? parseInt(digits, 10) : 0;
        onChange(v);
        setStr(v > 0 ? formatNumberWithDot(v) : '');
    };

    const commitOnEnter = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
    };

    return (
        <div className="stock-min">
            <label className="stock-min__input-wrap">
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Qualquer"
                    value={str}
                    onChange={handleChange}
                    onBlur={commit}
                    onKeyDown={commitOnEnter}
                    aria-label="Estoque mínimo"
                />
                <span className="stock-min__suffix">un.</span>
            </label>
            <div className="stock-min__presets" role="group" aria-label="Atalhos de estoque mínimo">
                {STOCK_PRESETS.map(preset => (
                    <button
                        key={preset.value}
                        type="button"
                        className={`stock-min__preset${value === preset.value ? ' is-active' : ''}`}
                        onClick={() => onChange(preset.value)}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

/**
 * Campo do trilho "trancado": mostra o controle real desativado (prévia
 * esmaecida) atrás de um overlay clicável que convida ao upgrade de plano.
 * Clique/Enter/Espaço abrem o modal do recurso correspondente. Mostrar (em vez
 * de esconder) deixa claro o que o usuário ganha ao mudar de plano.
 */
const LockedRailField = ({ label, onUpgrade, children }) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onUpgrade(); }
    };

    return (
        <div className="rail-field rail-locked">
            <label className="rail-label">{label}</label>
            <div className="rail-locked__box">
                <fieldset className="rail-locked__preview" disabled aria-hidden="true">
                    {children}
                </fieldset>
                <div
                    className="rail-locked__overlay"
                    role="button"
                    tabIndex={0}
                    onClick={onUpgrade}
                    onKeyDown={handleKeyDown}
                    aria-label={`${label}: recurso disponível em planos superiores. Fazer upgrade de plano.`}
                >
                    <span className="rail-locked__icon"><Icon name="lock" size={14} /></span>
                    <span className="rail-locked__text">Disponível em planos superiores</span>
                    <span className="rail-locked__cta">
                        <span className="rail-locked__cta-icon" aria-hidden="true">🚀</span>
                        Fazer upgrade
                    </span>
                </div>
            </div>
        </div>
    );
};

export const ModalProductList = ({ selectedProduct, setSelectedProduct, action, close, show }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_PAGE);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedInfo, setSelectedInfo] = useState(null); // { id, name }
    const [orientation, setOrientation] = useState(null);
    const [products, setProducts] = useState({ data: [] });
    const [loading, setLoading] = useState(false);
    // Termo digitado curto demais para buscar: é um estado próprio, não um
    // resultado vazio.
    const [needsMoreChars, setNeedsMoreChars] = useState(false);

    const [search, setSearch] = useState('');
    const [similarProduct, setSimilarProduct] = useState(null);

    // Filtros do trilho lateral
    const [companyId, setCompanyId] = useState(null);
    const [colorGroupId, setColorGroupId] = useState(null);
    const [priceBounds, setPriceBounds] = useState({ min: 0, max: 0 });
    const [priceRange, setPriceRange] = useState([0, 0]);
    const [stockMin, setStockMin] = useState(DEFAULT_STOCK_MIN);

    // Opções de select
    const [suppliers, setSuppliers] = useState([]);
    const [colorGroups, setColorGroups] = useState([]);

    const searchInputRef = useRef();

    // Alguns clientes não querem que os vendedores saibam quem são os
    // fornecedores; sem esta permissão o filtro de fornecedor não aparece.
    const canViewProductSupplier = hasPermission('show_product_supplier_in_budget');

    // Sem a permissão de custo, o preço já é ocultado nos cards; então o filtro
    // por preço (e a ordenação por preço) também não devem aparecer.
    const canViewProductPrice = hasPermission('show_product_supplier_price_in_budget');

    // Filtrar por preço e por estoque mínimo são recursos de plano distintos.
    // No admin o preço é gate por `calculation` (mesma feature que libera o
    // cálculo/exibição de preço em todo o painel); o estoque, por `stock`. Sem
    // cada recurso o respectivo campo aparece "trancado" (teaser) e o clique
    // abre o modal de upgrade com o conteúdo certo. Guardar a feature em estado
    // deixa um único modal atender aos dois campos.
    const canFilterPrice = hasPlanFeature('calculation');
    const canFilterStock = hasPlanFeature('stock');
    const [upgradeFeature, setUpgradeFeature] = useState(null);

    useEffect(() => {
        if (show) {
            setTimeout(() => {
                searchInputRef.current?.focus?.();
            }, 100);
            loadFilterData();
        }
    }, [show]);

    const loadFilterData = () => {
        if (canViewProductSupplier) {
            api.get(`/company/paginate?&filters[0][type][contain]=product&per_page=9999`)
                .then(res => setSuppliers(res.data.resources.data))
                .catch(() => {});
        }

        api.get(`/group-color`)
            .then(res => setColorGroups(res.data.resources))
            .catch(() => {});

        if (canViewProductPrice) {
            api.get(`/product/search-filters`)
                .then(res => {
                    const price = res.data.price || { min: 0, max: 0 };
                    setPriceBounds(price);
                    setPriceRange([price.min, price.max]);
                })
                .catch(() => {});
        }
    };

    const supplierOptions = useMemo(
        () => createOptions(suppliers, 'trade_name', 'id', true, null, 'Todos os fornecedores'),
        [suppliers]
    );

    // A ordenação fica sempre disponível por completo, independente da permissão
    // de custo — ordenar por preço não expõe o valor (os cards já ocultam o preço
    // para quem não tem a permissão).
    const sortOptions = ordernations;

    // Opções de grupo de cores com amostra visual (hexadecimal ou imagem).
    const colorGroupOptions = useMemo(() => [
        { value: null, label: 'Todos os grupos' },
        ...colorGroups.map(group => ({
            value: group.id,
            label: group.name,
            hexadecimal: group.hexadecimal,
            image: group.image_url?.thumbnail,
        })),
    ], [colorGroups]);

    const formatColorGroupOption = (option) => {
        if (option.value === null) return option.label;
        return (
            <span className="color-group-option">
                <span
                    className="color-group-swatch"
                    style={option.image
                        ? { backgroundImage: `url(${option.image})` }
                        : { backgroundColor: option.hexadecimal || '#EEECF7' }}
                />
                {option.label}
            </span>
        );
    };

    // Só contam como filtros "ativos" (chips removíveis, limpar tudo) quando o
    // recurso está liberado — assim quem não tem o plano não burla o gate pela X.
    const priceActive = canFilterPrice && priceBounds.max > 0 && !rangesEqual(priceRange, [priceBounds.min, priceBounds.max]);
    const stockActive = canFilterStock && stockMin > 0;

    // Objeto de filtros enviado ao backend — só o que está de fato ativo.
    const filters = useMemo(() => {
        const f = {};
        if (companyId) f.company_id = companyId;
        if (colorGroupId) f.color_group_id = colorGroupId;
        if (canFilterPrice && priceActive) { f.price_min = priceRange[0]; f.price_max = priceRange[1]; }
        if (canFilterStock) {
            if (stockActive) f.stock_min = stockMin;
        } else {
            // Sem o recurso de estoque: aplica só o baseline padrão (0 =
            // "Qualquer"), então itens zerados também aparecem.
            f.stock_min = DEFAULT_STOCK_MIN;
        }
        return f;
    }, [companyId, colorGroupId, canFilterPrice, canFilterStock, priceActive, priceRange, stockActive, stockMin]);

    const hasAnyFilter = companyId || colorGroupId || priceActive || stockActive;

    const clearPrice = () => setPriceRange([priceBounds.min, priceBounds.max]);
    const clearStock = () => setStockMin(0);

    const clearAllFilters = () => {
        setCompanyId(null);
        setColorGroupId(null);
        clearPrice();
        clearStock();
    };

    const handleClose = () => {
        setSearch('');
        setCurrentPage(1);
        clearAllFilters();
        // Na próxima abertura o filtro volta ao padrão (só produtos com estoque).
        setStockMin(DEFAULT_STOCK_MIN);
        close();
    };

    const handleSetProductsWithPagination = (response) => {
        if (!response || !response.data || !response.pagination) {
            setProducts({ data: [] });
            setLastPage(1);
            setPerPage(PER_PAGE);
            setTotalResults(0);
            setHasNextPage(false);
            setHasPrevPage(false);
            return;
        }

        const { data, pagination } = response;

        setProducts({ data });
        setCurrentPage(pagination.current_page);
        setLastPage(pagination.last_page);
        setPerPage(pagination.per_page);
        setTotalResults(pagination.total);
        setHasNextPage(pagination.current_page < pagination.last_page);
        setHasPrevPage(pagination.current_page > 1);
    };

    const selectedItem = (product) => {
        setSelectedProduct(product.id);
        setSelectedInfo({ id: product.id, name: product.name });
    };

    const supplierName = suppliers.find(s => s.id === companyId)?.trade_name;
    const colorGroupName = colorGroups.find(g => g.id === colorGroupId)?.name;

    return (
        <Modal
            className="modal-product-list"
            show={show}
            title="Escolha o produto"
            close={handleClose}
            size="xlarge"
            footer={
                <>
                    <div className="footer-pagination">
                        <Pagination
                            currentPage={currentPage}
                            lastPage={lastPage}
                            perPage={perPage}
                            totalResults={totalResults}
                            hasNextPage={hasNextPage}
                            hasPrevPage={hasPrevPage}
                            setCurrentPage={setCurrentPage}
                        />
                    </div>
                    <Button type="secondary" size="small" text="Voltar" action={handleClose} />
                    <Button type="primary" size="small" text="Confirmar" action={close} />
                </>
            }
        >
            <div className="product-picker">
                <aside className="product-picker__rail">
                    <SearchProducts
                        ref={searchInputRef}
                        onlyQuotable={true}
                        search={search}
                        setSearch={setSearch}
                        changeProducts={handleSetProductsWithPagination}
                        filterOptions={{ orientation }}
                        filters={filters}
                        perPage={PER_PAGE}
                        enabled={show}
                        page={currentPage}
                        setCurrentPage={setCurrentPage}
                        setLoading={setLoading}
                        setNeedsMoreChars={setNeedsMoreChars}
                        similarProduct={similarProduct}
                        setSimilarProduct={setSimilarProduct}
                    />

                    <div className="rail-head">
                        <span className="rail-title">Filtros</span>
                        {hasAnyFilter && (
                            <button type="button" className="rail-clear" onClick={clearAllFilters}>
                                Limpar tudo
                            </button>
                        )}
                    </div>

                    {canViewProductSupplier && (
                        <div className="rail-field">
                            <label className="rail-label">Fornecedor</label>
                            <InputSelect
                                options={supplierOptions}
                                value={companyId}
                                change={(value) => setCompanyId(value)}
                                removeMargin
                                styles={compactSelectStyles}
                            />
                        </div>
                    )}

                    <div className="rail-field">
                        <label className="rail-label">Grupo de cores</label>
                        <InputSelect
                            options={colorGroupOptions}
                            value={colorGroupId}
                            change={(value) => setColorGroupId(value)}
                            formatOptionLabel={formatColorGroupOption}
                            removeMargin
                            styles={compactSelectStyles}
                        />
                    </div>

                    {canViewProductPrice && (
                        canFilterPrice ? (
                            <div className="rail-field">
                                <label className="rail-label">Preço</label>
                                <RangeSlider
                                    min={priceBounds.min}
                                    max={priceBounds.max}
                                    step={100}
                                    value={priceRange}
                                    onChange={setPriceRange}
                                    inputs
                                    scale={100}
                                    prefix="R$"
                                />
                            </div>
                        ) : (
                            <LockedRailField label="Preço" onUpgrade={() => setUpgradeFeature('calculation')}>
                                <RangeSlider
                                    min={priceBounds.min}
                                    max={priceBounds.max}
                                    step={100}
                                    value={priceRange}
                                    onChange={setPriceRange}
                                    inputs
                                    scale={100}
                                    prefix="R$"
                                />
                            </LockedRailField>
                        )
                    )}

                    {canFilterStock ? (
                        <div className="rail-field">
                            <label className="rail-label">Estoque mínimo</label>
                            <StockMinFilter value={stockMin} onChange={setStockMin} />
                        </div>
                    ) : (
                        <LockedRailField label="Estoque mínimo" onUpgrade={() => setUpgradeFeature('stock')}>
                            <StockMinFilter value={stockMin} onChange={setStockMin} />
                        </LockedRailField>
                    )}
                </aside>

                <section className="product-picker__results">
                    <div className="results-head">
                        {hasAnyFilter && (
                            <div className="active-chips">
                                {canViewProductSupplier && companyId && (
                                    <span className="active-chip">
                                        {supplierName || 'Fornecedor'}
                                        <button aria-label="Remover filtro de fornecedor" onClick={() => setCompanyId(null)}><Icon name="close" size={12} /></button>
                                    </span>
                                )}
                                {colorGroupId && (
                                    <span className="active-chip">
                                        {colorGroupName || 'Grupo de cores'}
                                        <button aria-label="Remover filtro de cores" onClick={() => setColorGroupId(null)}><Icon name="close" size={12} /></button>
                                    </span>
                                )}
                                {canViewProductPrice && priceActive && (
                                    <span className="active-chip">
                                        {convertIntToMoney(priceRange[0], true)} – {convertIntToMoney(priceRange[1], true)}
                                        <button aria-label="Remover filtro de preço" onClick={clearPrice}><Icon name="close" size={12} /></button>
                                    </span>
                                )}
                                {stockActive && (
                                    <span className="active-chip">
                                        Estoque ≥ {formatNumberWithDot(stockMin)} un.
                                        <button aria-label="Remover filtro de estoque" onClick={clearStock}><Icon name="close" size={12} /></button>
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="results-sort">
                            <InputSelect
                                options={sortOptions}
                                value={orientation}
                                change={(value) => setOrientation(value)}
                                removeMargin
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="results-grid" aria-hidden="true">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className="card-skeleton" />
                            ))}
                        </div>
                    ) : needsMoreChars ? (
                        <div className="results-state results-empty">
                            <Icon name="search" size={32} />
                            <p>Digite ao menos {MIN_SEARCH_LENGTH} caracteres.</p>
                            <span>Ou use os filtros ao lado para navegar sem buscar.</span>
                        </div>
                    ) : products.data?.length > 0 ? (
                        <div className="results-grid">
                            {products.data.map((product, index) => (
                                <CardProductInfo
                                    key={product.id || index}
                                    product={product}
                                    checkBoxAction={() => selectedItem(product)}
                                    isChecked={selectedInfo?.id === product.id}
                                    totalStock={calculateTotalStock(product.subproducts)}
                                    subProducts={product.subproducts}
                                    title={product.name}
                                    sku={product.sku}
                                    price={product.price}
                                    index={index}
                                    setSimilarProduct={setSimilarProduct}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="results-state results-empty">
                            <Icon name="search" size={32} />
                            <p>Nenhum produto encontrado.</p>
                            <span>Ajuste a busca ou os filtros ao lado.</span>
                        </div>
                    )}

                </section>
            </div>

            <PlanFeatureUpgradeModal
                feature={upgradeFeature}
                show={upgradeFeature !== null}
                close={() => setUpgradeFeature(null)}
            />
        </Modal>
    );
};
